if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.variantAssetsEnabled = this.hasAttribute('data-variant-assets-enabled');
      this.variantAssetsOnly = this.hasAttribute('data-variant-assets-only');
      this.variantAssetsFallbackSingle = this.hasAttribute('data-variant-assets-fallback-single');
      // Real variant switches keep this current via applyVariantMedia(), but
      // nothing else sets it before then — seed it from the server-rendered
      // initial variant so a background hydration finishing before any
      // on:variant:change fires doesn't read this as "no variant selected"
      // and hide the group that's already showing (see setActiveVariantAssets).
      this.lastVariantId = this.dataset.initialVariantId || null;
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.buildThumbs();
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');
      this.progressBar = this.querySelector('.media-progress-bar');
      this.progressSegments = this.progressBar
        ? Array.from(this.progressBar.querySelectorAll('.media-progress-bar__segment'))
        : [];

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors(this.lastVariantId));
      }

      // Assets-only mode: the server render already hid the native media, but
      // setActiveMediaGroup() above owns native visibility and has just shown
      // the selected group's share of it again. Re-apply on top, the same
      // order applyVariantMedia() uses. Deliberately not a full
      // setActiveVariantAssets() call — that one can initialise the slider
      // early and would pre-empt the stacked-layout branch below.
      if (this.variantAssetsOnly) {
        this.applyVariantAssetsOnly(this.hasVisibleVariantAssets());
        this.reconcileSingleItem();
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
      this.section.addEventListener('on:variant:media-change', this.onVariantMediaChange.bind(this));

      if (this.variantAssetsEnabled) {
        // requestIdleCallback fires as soon as the main thread has spare
        // time, which on an image-heavy PDP is usually well before the
        // 'load' event (that waits on every resource on the page, including
        // ones this warm-up doesn't care about). Safari has no support as
        // of 16.3 (https://webkit.org/b/164193); calling immediately there
        // is fine since the actual work is an async fetch either way.
        const idle = window.requestIdleCallback || ((callback) => callback());
        idle(() => this.warmVariantAssetGroups());
      }
    }

    /**
     * Builds the thumb-strip <li> items from the viewer's own DOM instead of
     * a second server-rendered image pass — the viewer's markup (data-media-id,
     * data-variant-ids, data-native-variant-media, initial display state, and
     * the source image itself) is already everything a thumb needs. Active
     * state/aria-current is applied afterwards by setActiveThumb via the
     * normal initGallery()/customSetActiveMedia() flow, so it isn't computed
     * here. Runs once, synchronously, before initGallery() reads currentThumb.
     */
    buildThumbs() {
      if (!this.thumbs || this.thumbs.children.length > 0) return;

      this.thumbCounters = { image: 0, model: 0, video: 0 };
      const fragment = document.createDocumentFragment();

      this.querySelectorAll('.media-viewer__item[data-media-id]').forEach((viewerItem) => {
        const li = this.buildThumbItem(viewerItem);
        if (li) fragment.appendChild(li);
      });

      this.thumbs.appendChild(fragment);
    }

    /**
     * Builds one thumb-strip <li> from a viewer item's own DOM (data-media-id,
     * data-variant-ids, data-native-variant-media, current display state, and
     * the source image itself are already everything a thumb needs). Used by
     * buildThumbs() at init for every item present up front, and again by
     * hydrateVariantAssetGroup() for items merged in later from a background
     * fetch — this.thumbCounters carries the running image/model/video counts
     * across both so aria-labels keep counting up instead of restarting.
     * @param {Element} viewerItem - A .media-viewer__item element.
     * @returns {?Element} The built <li>, or null if there's no thumb strip
     *   or the item's media type isn't one thumbs are built for.
     */
    buildThumbItem(viewerItem) {
      if (!this.thumbs) return null;

      const srcWidth = Number(this.thumbs.dataset.thumbSrcWidth) || 80;
      const imgClass = this.thumbs.dataset.thumbImgClass || '';
      const paddingTop = this.thumbs.dataset.thumbPaddingTop;
      const btnClass = this.thumbs.dataset.thumbBtnClass || 'media-thumbs__btn';
      const badgeTemplate = this.querySelector('.js-media-thumb-badges');

      const mediaType = viewerItem.dataset.mediaType;
      const typeKey = mediaType === 'external_video' ? 'video' : mediaType;
      if (!(typeKey in this.thumbCounters)) return null;
      this.thumbCounters[typeKey] += 1;

      const li = document.createElement('li');
      li.className = 'media-thumbs__item';
      if (viewerItem.classList.contains('media-viewer__item--variant')) {
        li.classList.add('media-thumbs__item--variant');
      }
      li.dataset.mediaId = viewerItem.dataset.mediaId;
      if (viewerItem.dataset.variantIds) li.dataset.variantIds = viewerItem.dataset.variantIds;
      if ('nativeVariantMedia' in viewerItem.dataset) li.dataset.nativeVariantMedia = '';
      li.style.display = viewerItem.style.display;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = btnClass;
      btn.setAttribute('aria-controls', 'gallery-viewer');
      if (paddingTop) btn.style.paddingTop = paddingTop;

      const label = document.createElement('span');
      label.className = 'visually-hidden';
      if (typeKey === 'image') {
        label.textContent = theme.strings.loadImage.replace('[index]', this.thumbCounters.image);
      } else if (typeKey === 'model') {
        label.textContent = theme.strings.loadModel.replace('[index]', this.thumbCounters.model);
      } else {
        label.textContent = theme.strings.loadVideo.replace('[index]', this.thumbCounters.video);
      }
      btn.appendChild(label);

      if (badgeTemplate && typeKey !== 'image') {
        const badge = badgeTemplate.content.querySelector(`[data-badge="${typeKey}"]`);
        if (badge) btn.appendChild(badge.cloneNode(true));
      }

      const sourceImg = viewerItem.querySelector('img.product-image');
      if (sourceImg) {
        const img = document.createElement('img');
        img.className = imgClass;
        // Packshot spacing (ClickUp 869f1nrd3): background-free images opt in
        // to `.img--packshot` (contain + inset) per image via their alt marker,
        // resolved in Liquid on the viewer image this thumb is built from — so
        // copy the marker across rather than re-resolving it. Without it a
        // packshot thumb is cropped edge-to-edge whenever 'Thumb crop' is
        // Center/Top. The --packshot-pad vars come from the thumb <ul>.
        if (sourceImg.classList.contains('img--packshot')) {
          img.classList.add('img--packshot');
        }
        img.loading = 'lazy';
        img.alt = sourceImg.alt;
        img.src = MediaGallery.resizeImageUrl(sourceImg.src, srcWidth);
        img.srcset = `${img.src}, ${MediaGallery.resizeImageUrl(sourceImg.src, srcWidth * 2)} 2x`;
        btn.appendChild(img);
      }

      li.appendChild(btn);
      return li;
    }

    /**
     * Swaps (or appends) the Shopify CDN 'width' query param on an image URL.
     * @param {string} url - Source CDN image URL.
     * @param {number} width - Desired width.
     * @returns {string} - Resized image URL.
     */
    static resizeImageUrl(url, width) {
      const w = Math.round(width);
      if (/[?&]width=\d+/.test(url)) {
        return url.replace(/([?&])width=\d+/, `$1width=${w}`);
      }
      return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      this.applyVariantMedia(
        evt.detail.variant ? evt.detail.variant.id : null,
        evt.detail.variant && evt.detail.variant.featured_media ? evt.detail.variant.featured_media.id : null
      );
    }

    /**
     * Handle the early, client-resolved variant-media event dispatched by
     * variant-picker.js on 'change' — fires well before on:variant:change,
     * which waits on a section-render fetch that this gallery swap doesn't
     * actually need.
     * @param {Event} evt - event dispatched by variant-picker
     */
    onVariantMediaChange(evt) {
      this.applyVariantMedia(evt.detail.variantId, evt.detail.featuredMediaId);
    }

    /**
     * Shows the media matching a variant, by id and/or featured media id.
     * Idempotent — a later on:variant:change for the same variant is a
     * no-op (customSetActiveMedia early-returns on an unchanged item).
     * @param {?number} variantId - Selected variant id.
     * @param {?number} featuredMediaId - Selected variant's featured media id.
     */
    applyVariantMedia(variantId, featuredMediaId) {
      this.lastVariantId = variantId || null;
      if (this.variantAssetsEnabled) this.ensureVariantAssetsHydrated(variantId);

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors(variantId));
      }

      if (this.variantAssetsEnabled) {
        this.setActiveVariantAssets(variantId || null);
      }

      // When variant assets are enabled, prefer leading with the variant's own
      // metafield images (revealed by setActiveVariantAssets above) rather than
      // jumping past them to the native featured_media. Only fall back to
      // featured_media when no variant assets exist.
      let variantAssetItem = null;
      if (variantId && this.variantAssetsEnabled) {
        variantAssetItem = this.viewer.querySelector(
          `.media-viewer__item[data-variant-ids~="${variantId}"]`
        );
      }

      if (variantAssetItem && variantAssetItem.style.display !== 'none') {
        this.customSetActiveMedia(variantAssetItem, true);
      } else if (variantAssetItem && variantAssetItem.style.display === 'none') {
        // The variant asset item was hidden (e.g., by keepFirstNativeOnly):
        // fall back to the first visible media item.
        const target = this.visibleItems.find((el) => el.dataset.mediaId);
        if (target) {
          this.customSetActiveMedia(target, true);
        }
      } else if (featuredMediaId) {
        const variantMedia = this.viewer.querySelector(`[data-media-id="${featuredMediaId}"]`);
        if (variantMedia && variantMedia.style.display !== 'none') {
          this.customSetActiveMedia(variantMedia, true);
        } else if (variantMedia && variantMedia.style.display === 'none') {
          // The featured media was hidden (e.g., by keepFirstNativeOnly):
          // fall back to the first visible media item.
          const target = this.visibleItems.find((el) => el.dataset.mediaId);
          if (target) {
            this.customSetActiveMedia(target, true);
          }
        }
      } else if (this.currentItem && this.currentItem.style.display === 'none') {
        // Neither variant assets nor featured media to lead with (the asset
        // lookup above already came back empty): recover from a now-hidden
        // current slide by landing on the first visible media item.
        const target = this.visibleItems.find((el) => el.dataset.mediaId);
        if (target) {
          this.customSetActiveMedia(target, true);
        }
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @param {?number} variantId - Currently selected variant id, used as a
     *   fallback when there's no .option-selector to read (see below).
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors(variantId) {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      // No .option-selector in the DOM — product-configurator products never
      // render variant-picker.liquid (it's suppressed in main-product.liquid
      // for is_configurator products), so this always used to return null
      // here, which left media grouping permanently unable to resolve a
      // group. Fall back to resolving the group from the selected variant's
      // own native-media tagging instead.
      return this.getMediaGroupFromVariantId(variantId);
    }

    /**
     * Resolves the media group a variant belongs to via the native-media
     * data-variant-ids tagging built into the group map (see
     * getMediaGroupMap), instead of reading the option value directly.
     * @param {?number} variantId
     * @returns {?string} - The matching group name, or null.
     */
    getMediaGroupFromVariantId(variantId) {
      if (!variantId) return null;
      const idStr = String(variantId);
      const { groups } = this.getMediaGroupMap();
      return Object.keys(groups).find((name) => groups[name].items.some(
        (item) => item.main.dataset.variantIds && item.main.dataset.variantIds.split(' ').includes(idStr)
      )) || null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          // Variant metafield assets are owned by setActiveVariantAssets; if
          // they joined the map they would inherit the previous item's group
          // (the carry-over below) and get shown with the wrong color group.
          // Native variant media (data-native-variant-media) DO participate:
          // they are the map's labelled boundaries, and setActiveVariantAssets
          // runs after grouping so its per-variant refinement wins.
          if (item.dataset.variantIds && !('nativeVariantMedia' in item.dataset)) return;
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            if (this.progressSegments.length) {
              groupItem.segment = this.progressSegments.find(
                (seg) => seg.dataset.mediaId === item.dataset.mediaId
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups.length; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          // Variant metafield asset items (data-variant-ids) are skipped here
          // and in the else-branch: their visibility is owned exclusively by
          // setActiveVariantAssets.
          this.viewerItems.forEach((item) => {
            if (item.dataset.variantIds && !('nativeVariantMedia' in item.dataset)) return;
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            if (item.dataset.variantIds && !('nativeVariantMedia' in item.dataset)) return;
            item.style.display = 'none';
          });
          this.progressSegments.forEach((seg) => {
            if (seg.dataset.variantIds && !('nativeVariantMedia' in seg.dataset)) return;
            seg.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.segment) {
              item.segment.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        // No group resolved: ALL data-variant-ids items (metafield assets
        // and native variant media alike) are left alone — per-variant
        // visibility belongs to setActiveVariantAssets, and at init (where
        // this can run on a group-resolution miss) no correcting pass
        // follows, so force-showing tagged natives would leak every color.
        this.viewerItems.forEach((item) => {
          if (item.dataset.variantIds) return;
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          if (item.dataset.variantIds) return;
          item.style.display = '';
        });
        this.progressSegments.forEach((seg) => {
          if (seg.dataset.variantIds) return;
          seg.style.display = '';
        });
      }
    }

    /**
     * Shows only the variant metafield asset items (dam.variant_associated_assets)
     * belonging to the selected variant, across the viewer, thumbnails and
     * progress bar. Product media visibility is owned by setActiveMediaGroup
     * and is not touched here.
     * @param {?number} variantId - Currently selected variant id (null when none).
     */
    setActiveVariantAssets(variantId) {
      const idStr = variantId === null ? null : String(variantId);
      this.querySelectorAll('[data-variant-ids]').forEach((el) => {
        if (idStr === null) {
          if ('nativeVariantMedia' in el.dataset) {
            // No selection: pre-selection overview shows the native variant
            // images — except when media grouping is active, which owns
            // native visibility on incomplete selections; don't undo it.
            if (!this.mediaGroupingEnabled) el.style.display = '';
            return;
          }
          el.style.display = 'none';
          return;
        }
        el.style.display = el.dataset.variantIds.split(' ').includes(idStr) ? '' : 'none';
      });

      // Assets-only mode, applied on top of the per-variant pass above and
      // BEFORE the placeholder check below, so the placeholder sees the final
      // visibility rather than natives this is about to hide.
      if (this.variantAssetsOnly) {
        this.applyVariantAssetsOnly(this.hasVisibleVariantAssets());
      }

      // Metafield-only products render a placeholder slide; show it only
      // while no other viewer item is visible (no selection, or a variant
      // without assets), so the gallery frame never collapses to nothing.
      const placeholder = this.querySelector('[data-media-placeholder]');
      if (placeholder) {
        const anyVisible = Array.from(this.querySelectorAll('.media-viewer__item'))
          .some((el) => el !== placeholder && el.style.display !== 'none');
        placeholder.style.display = anyVisible ? 'none' : '';
      }

      // After the placeholder has settled, so the count it reads is final.
      if (this.variantAssetsOnly) this.reconcileSingleItem();

      this.setVisibleItems();

      // The gallery skips slider initialisation when it loads with a single
      // visible image; initialise it now that asset items made it a slider.
      if (!this.galleryInitialised && this.visibleItems.length > 1
          && !(this.dataset.layout === 'stacked' && theme.mediaMatches.md)) {
        this.initGallery();
      }

      // Refresh the slide offset after visibility changes when we have enough
      // items to calculate it, so handleScroll doesn't treat swipes as resizes.
      if (this.galleryInitialised && this.visibleItems.length >= 2) {
        this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      }

      // Refresh slider state that would otherwise only update through
      // customSetActiveMedia, which early-returns when the item is unchanged.
      if (this.currentItem && this.currentItem.style.display !== 'none') {
        this.currentIndex = this.visibleItems.indexOf(this.currentItem);
        if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
        if (this.nextBtn) this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        if (this.counterCurrent) this.counterCurrent.textContent = this.currentIndex + 1;
      } else if (this.galleryInitialised) {
        // The current slide was just hidden: re-anchor to the incoming
        // variant's own assets when visible (the same node onVariantChange
        // targets, so its follow-up call dedupes), else the first visible
        // media item, else park on the placeholder with navigation disabled —
        // stale prev/next state here would crash handleNavClick on undefined.
        const firstVisible = this.visibleItems.find((el) => el.dataset.variantIds)
          || this.visibleItems.find((el) => el.dataset.mediaId);
        if (firstVisible) {
          this.customSetActiveMedia(firstVisible, true);
        } else {
          this.currentItem = null;
          this.currentIndex = -1;
          if (this.prevBtn) this.prevBtn.disabled = true;
          if (this.nextBtn) this.nextBtn.disabled = true;
          if (this.counterCurrent) {
            this.counterCurrent.textContent = this.visibleItems.length > 0 ? 1 : 0;
          }
        }
      }
    }

    /**
     * Whether the selected variant resolved at least one visible metafield
     * asset slide — the condition assets-only mode keys on. Scoped to viewer
     * items so the matching thumbs and progress segments can't double-count.
     * @returns {boolean}
     */
    hasVisibleVariantAssets() {
      const hasVisible = Array.from(this.querySelectorAll('.media-viewer__item[data-variant-ids]'))
        .some((el) => !('nativeVariantMedia' in el.dataset) && el.style.display !== 'none');
      if (hasVisible) return true;

      // A group whose fetch hasn't settled yet has no slides in the DOM for the
      // pass above to find, so without this the natives are restored for the
      // length of that fetch and flash product shots the variant doesn't own.
      // Keyed on 'settled', not 'started': ensureVariantAssetsHydrated() sets
      // 'started' synchronously before this runs on a variant change, so a
      // 'started' test would never fire on the one path it exists for.
      if (this.lastVariantId && this.pendingVariantGroups) {
        const group = this.pendingVariantGroups.get(String(this.lastVariantId));
        if (group && !group.settled) return true;
      }
      return false;
    }

    /**
     * Assets-only mode (ClickUp 869f1kev3): hides ALL native product media
     * while the selected variant has DAM images of its own, so those images
     * are the entire gallery, and restores it when the variant has none.
     *
     * Runs across viewer items, thumbs and progress segments in one pass —
     * they share data-media-id, and the metafield asset items are the only
     * things skipped, since setActiveVariantAssets owns those.
     *
     * Restore is asymmetric on purpose. Native media TAGGED to a variant is
     * re-set correctly by the per-variant pass on every call, so touching it
     * here could only undo that (showing another colour's default). Only the
     * untagged media — which no other pass looks at — needs restoring, and
     * where media grouping is on it owns that media's visibility, so the
     * active group decides rather than a blanket reveal.
     * @param {boolean} hideNatives - Whether native media should be hidden.
     */
    applyVariantAssetsOnly(hideNatives) {
      this.querySelectorAll('[data-media-id]').forEach((el) => {
        if (el.dataset.variantIds && !('nativeVariantMedia' in el.dataset)) return;

        if (hideNatives) {
          el.style.display = 'none';
        } else if (!el.dataset.variantIds) {
          if (this.mediaGroupingEnabled && this.currentGroup) {
            const inGroup = this.currentGroup.items.some(
              (item) => item.main.dataset.mediaId === el.dataset.mediaId
            );
            el.style.display = inGroup ? '' : 'none';
          } else {
            el.style.display = '';
          }
        }
      });

      // lastVariantId, not noSelectedVariant: the latter is a page-load fact
      // that stays true after the shopper picks a swatch, which would disable
      // the trim for the rest of the session. lastVariantId is seeded from the
      // server-rendered initial variant (null when none is selected) and
      // updated on every variant change, so it tracks the live answer to
      // "is a variant selected?" — the same question the Liquid half asks.
      if (!hideNatives && this.variantAssetsFallbackSingle && this.lastVariantId) {
        this.keepFirstNativeOnly();
      }
    }

    /**
     * Single-image fallback (same ticket, round 2): trims the natives the
     * restore pass above just revealed down to the FIRST one in viewer order,
     * so a variant with no DAM images of its own falls back to one product
     * image rather than the whole set.
     *
     * Deliberately a second pass rather than a branch inside that loop: the
     * per-element decision there — media grouping, the native variant map —
     * still owns WHICH natives are candidates, and this only trims what
     * survived it. So the rule stays correct if either of those changes, and
     * it needs the full set decided before it can know which one is first.
     *
     * Keyed by data-media-id so the viewer item, its thumb and its progress
     * segment move together. Metafield asset items are skipped by the same
     * test applyVariantAssetsOnly uses — setActiveVariantAssets owns those.
     */
    keepFirstNativeOnly() {
      const isNative = (el) => !(el.dataset.variantIds && !('nativeVariantMedia' in el.dataset));

      const items = Array.from(this.querySelectorAll('.media-viewer__item[data-media-id]'));
      const keeper = items.find((el) => isNative(el) && el.style.display !== 'none');
      if (!keeper) return;

      this.querySelectorAll('[data-media-id]').forEach((el) => {
        if (!isNative(el) || el.dataset.mediaId === keeper.dataset.mediaId) return;
        el.style.display = 'none';
      });
    }

    /**
     * Reconciles the geometry class after assets-only mode changes what is
     * visible. The stacked layout sizes every viewer item at 50% width unless
     * it carries media-viewer__item--single, and setActiveMediaGroup only ever
     * applies that class to NATIVE items — metafield asset items are skipped
     * there. Hiding all the natives can therefore leave a lone DAM asset that
     * would render half-width on desktop stacked, so the class is re-derived
     * here from what is actually on screen.
     */
    reconcileSingleItem() {
      const items = Array.from(this.querySelectorAll('.media-viewer__item'));
      const shown = items.filter((el) => el.style.display !== 'none');
      items.forEach((el) => {
        el.classList.toggle('media-viewer__item--single', shown.length === 1 && shown[0] === el);
      });
    }

    /**
     * Background-fetches every color group not already rendered server-side
     * (only the initially active group ships in the base HTML — see
     * '_variant-media-assets') and merges each into the live gallery as it
     * arrives, so the base document stays light on high-variant products
     * while a swatch click still usually finds the markup already in place
     * (PDP page-weight work, fix/performance-issues). Runs once, as soon as
     * the main thread is idle, with a concurrency cap — Shopify serves these
     * over HTTP/2, so several in flight at once multiplex over one
     * connection rather than queuing behind each other.
     */
    warmVariantAssetGroups() {
      const dataEl = this.querySelector('.js-variant-asset-groups');
      if (!dataEl) return;

      let groups;
      try {
        groups = JSON.parse(dataEl.textContent);
      } catch (err) {
        return;
      }
      dataEl.remove();
      if (!Array.isArray(groups) || groups.length === 0) return;

      if (!this.dataset.sectionId || !this.dataset.productUrl) return;

      // Indexed by every member variant id (not just the rep) so a shopper
      // selecting any variant in a not-yet-fetched group can be matched to
      // it and jump the queue — see ensureVariantAssetsHydrated(). Group
      // objects are shared between this map and the sequential queue below,
      // so marking one 'started' in either place is seen by both.
      this.pendingVariantGroups = new Map();
      groups.forEach((group) => {
        group.vids.split(' ').forEach((vid) => this.pendingVariantGroups.set(vid, group));
      });

      let nextIndex = 0;
      this.warmNextVariantGroup = () => {
        while (nextIndex < groups.length && groups[nextIndex].started) nextIndex += 1;
        if (nextIndex >= groups.length) return;
        const group = groups[nextIndex];
        nextIndex += 1;
        this.fetchVariantAssetGroup(group).then(this.warmNextVariantGroup);
      };

      const CONCURRENCY = 6;
      for (let i = 0; i < Math.min(CONCURRENCY, groups.length); i += 1) this.warmNextVariantGroup();
    }

    /**
     * Fetches and hydrates a single group, guarding against a group already
     * dispatched (by the background queue or a prior priority call) being
     * fetched twice.
     * @param {object} group - {variantId, vids, started, settled} entry.
     * @returns {Promise} Resolves once fetch + DOM merge complete (or the
     *   fetch failed) — never rejects, so it's safe to chain unconditionally.
     */
    fetchVariantAssetGroup(group) {
      if (group.started) return Promise.resolve();
      group.started = true;

      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', group.variantId);
      url.searchParams.set('section_id', this.dataset.sectionId);

      return theme.fetchCache.fetch(url.toString())
        .then((html) => this.hydrateVariantAssetGroup(html))
        .catch(() => {})
        // 'settled' means this group will never produce more slides than it has
        // now — read by hasVisibleVariantAssets() to tell "assets are incoming"
        // from "this group genuinely has none". Set after the catch so a failed
        // fetch can't hold the natives hidden forever, and set even when the
        // group hydrated zero image slides (videos/PDFs only), which is what
        // restores the natives in that case rather than emptying the gallery.
        .then(() => {
          group.settled = true;
          // Re-run the visibility pass now that the group can no longer add
          // slides, so a failed fetch or a video/PDF-only metafield restores
          // the natives instead of leaving the gallery empty.
          if (this.variantAssetsOnly) {
            this.setActiveVariantAssets(this.lastVariantId || null);
          }
        });
    }

    /**
     * Called on every variant change: if the newly selected variant's asset
     * group hasn't been fetched yet, fetches it immediately instead of
     * leaving it to wait its turn in the background warm-up queue — so
     * quickly picking a color still ahead of the queue doesn't show an
     * empty gallery until warm-up happens to reach it.
     * @param {?string} variantId - Newly selected variant id.
     */
    ensureVariantAssetsHydrated(variantId) {
      if (!variantId || !this.pendingVariantGroups) return;
      const group = this.pendingVariantGroups.get(String(variantId));
      if (group && !group.started) this.fetchVariantAssetGroup(group);
    }

    /**
     * Merges one background-fetched color group's markup into the live
     * gallery — viewer slides, progress-bar segments, and thumbs (thumbs
     * are otherwise only built once at init, from buildThumbItem). Inserted
     * before the first native-media item so a newly hydrated group still
     * leads over product photos when it becomes active, matching the
     * original server-rendered order.
     * @param {string} html - Section HTML re-rendered with that group's
     *   representative variant selected (so only its group is present).
     */
    hydrateVariantAssetGroup(html) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const isAssetItem = (el) => el.dataset.variantIds && !('nativeVariantMedia' in el.dataset);

      const newViewerItems = Array.from(
        tmpl.content.querySelectorAll('.media-viewer__item[data-variant-ids]')
      ).filter(isAssetItem);

      if (newViewerItems.length) {
        const viewerAnchor = Array.from(this.viewer.querySelectorAll('.media-viewer__item[data-media-id]'))
          .find((el) => !isAssetItem(el)) || null;
        const thumbAnchor = this.thumbs
          ? Array.from(this.thumbs.querySelectorAll('.media-thumbs__item[data-media-id]'))
            .find((el) => !isAssetItem(el)) || null
          : null;

        newViewerItems.forEach((item) => {
          if (this.viewer.querySelector(`[data-media-id="${item.dataset.mediaId}"]`)) return;

          this.viewer.insertBefore(item, viewerAnchor);

          const thumb = this.buildThumbItem(item);
          if (thumb) this.thumbs.insertBefore(thumb, thumbAnchor);
        });
      }

      if (this.progressBar) {
        const newSegments = Array.from(
          tmpl.content.querySelectorAll('.media-progress-bar__segment[data-variant-ids]')
        ).filter(isAssetItem);

        if (newSegments.length) {
          const segmentAnchor = Array.from(
            this.progressBar.querySelectorAll('.media-progress-bar__segment[data-media-id]')
          ).find((el) => !isAssetItem(el)) || null;

          newSegments.forEach((seg) => {
            if (this.progressBar.querySelector(`[data-media-id="${seg.dataset.mediaId}"]`)) return;
            this.progressBar.insertBefore(seg, segmentAnchor);
          });

          this.progressSegments = Array.from(
            this.progressBar.querySelectorAll('.media-progress-bar__segment')
          );
        }
      }

      if (!newViewerItems.length) return;

      // The selected variant may have changed while this group's fetch was
      // in flight — reconcile visibility against whatever is current now,
      // BEFORE the zoom modal copies it below: these items arrive with no
      // display style at all (visible), and only this call decides which of
      // them should actually stay hidden.
      if (this.variantAssetsEnabled) {
        this.setActiveVariantAssets(this.lastVariantId || null);

        // setActiveVariantAssets only re-anchors the active slide when the
        // PREVIOUSLY current item just got hidden — but the item shown
        // while this fetch was in flight is typically the untagged
        // featured-media fallback (see applyVariantMedia), which it never
        // touches. Without this, a color selected before its group finished
        // hydrating stays on that stale fallback even though the correct,
        // now-visible image for it just landed in the strip.
        if (this.lastVariantId) {
          const idStr = String(this.lastVariantId);
          const nowCurrent = newViewerItems.find(
            (item) => item.dataset.variantIds?.split(' ').includes(idStr)
          );
          if (nowCurrent && nowCurrent !== this.currentItem) {
            this.customSetActiveMedia(nowCurrent, true);
          }
        }
      }

      // The zoom modal builds its own thumbs from this same viewer, once,
      // whenever it first connects — refresh it now in case that already
      // happened (including while the modal is currently open) before this
      // group arrived, otherwise this group's items never get a zoom thumb.
      // Run after setActiveVariantAssets above: buildThumbs copies each
      // item's current display state onto its thumb's hidden attribute, so
      // it has to see the final, reconciled visibility, not whatever these
      // items arrived with.
      this.galleryModal?.querySelector('gallery-zoom')?.buildThumbs();

      // Warm this group's primary image in the background now that it's in
      // the DOM, so a click right after hydration doesn't start a cold
      // fetch — mirrors the staggered hidden-primary warm-up in
      // initGallery(), just triggered per-group as hydration completes
      // instead of all at once at init.
      const primaryImg = newViewerItems[0].querySelector('.product-image[loading="lazy"]');
      if (primaryImg) primaryImg.loading = 'eager';
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) {
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        return;
      }

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();
      this.galleryInitialised = true;

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the visible slider images for smooth UX.
        const visibleImages = Array.from(this.viewer.querySelectorAll('.product-image[loading="lazy"]'))
          .filter((img) => {
            const assetItem = img.closest('[data-variant-ids]');
            return !assetItem || assetItem.style.display !== 'none';
          });

        // Also warm each OTHER variant's primary DAM image (the first
        // asset in its data-variant-ids group) so a later variant switch
        // doesn't start a cold image fetch on click/tap. Only the primary,
        // not every detail shot, keeps the background download bounded.
        const hiddenPrimaryImages = [];
        if (this.variantAssetsEnabled) {
          const seenGroups = new Set();
          this.viewer.querySelectorAll('[data-variant-ids]').forEach((item) => {
            if (item.style.display !== 'none') return;
            const key = item.dataset.variantIds;
            if (seenGroups.has(key)) return;
            seenGroups.add(key);
            const img = item.querySelector('.product-image[loading="lazy"]');
            if (img) hiddenPrimaryImages.push(img);
          });
        }

        // Eager load the visible slides first, staggered generously (there
        // are only ever a handful) to avoid a bandwidth burst.
        visibleImages.forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });

        // Hidden variant primaries can number in the dozens, so stagger
        // them much tighter — otherwise the last of e.g. 50 variants would
        // not even start loading until 25s after page load.
        const hiddenStart = 500 * (visibleImages.length + 1);
        hiddenPrimaryImages.forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, hiddenStart + 100 * index);
        });

        // DAM thumbnails are tiny (~100px) regardless of variant count, so
        // — unlike the full-size images above — warm every hidden one
        // immediately rather than staggering/capping them.
        if (this.variantAssetsEnabled && this.thumbs) {
          this.thumbs.querySelectorAll('[data-variant-ids] img[loading="lazy"]').forEach((img) => {
            const assetItem = img.closest('[data-variant-ids]');
            if (assetItem && assetItem.style.display === 'none') {
              img.loading = 'eager';
            }
          });
        }
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      if (currentItem && currentItem !== this.currentItem) {
        this.customSetActiveMedia(currentItem, true);
      } else {
        // Already on the current item — paint the initial active states that
        // customSetActiveMedia's early-return guard would otherwise skip, so the
        // first thumb/progress segment is highlighted before any swipe.
        if (this.thumbs) this.setActiveThumb();
        if (this.progressSegments.length) this.setActiveSegment();
      }
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      if (this.progressBar) this.progressBar.addEventListener('click', this.handleProgressClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      // A variant switch can collapse the visible set to one item (or just
      // the placeholder); the browser clamps scrollLeft and fires 'scroll',
      // and the offset math below needs two visible items.
      if (this.visibleItems.length < 2) return;
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      const btn = evt.target.closest('.media-ctrl__btn');
      if (!btn) return;

      const itemToShow = btn === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];
      if (!itemToShow) return;

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles 'click' events on the mobile progress bar.
     * @param {object} evt - Event object.
     */
    handleProgressClick(evt) {
      const segment = evt.target.closest('.media-progress-bar__segment');
      if (!segment) return;
      const itemToShow = this.viewer.querySelector(`[data-media-id="${segment.dataset.mediaId}"]`);
      if (!itemToShow) return;
      this.customSetActiveMedia(itemToShow, true, true);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      if (this.visibleItems.length < 2) return;
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     * @param {boolean} [smoothScroll=false] - Animate the scroll (mobile dots match the arrows' glide).
     */
    customSetActiveMedia(mediaItem, scrollToItem = true, smoothScroll = false) {
      // Callers can pass null (stacked init finds no .is-current-variant on a
      // metafield-only gallery) — a no-op beats a getBoundingClientRect crash.
      if (!mediaItem || mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) {
        this.viewer.scrollTo({
          left: this.currentItem.offsetLeft,
          behavior: smoothScroll ? 'smooth' : 'auto'
        });
      }
      if (this.thumbs) this.setActiveThumb();
      if (this.progressSegments.length) this.setActiveSegment();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Sets the active progress bar segment.
     */
    setActiveSegment() {
      this.progressSegments.forEach((seg) => seg.classList.remove('is-active'));
      const active = this.progressSegments.find(
        (seg) => seg.dataset.mediaId === this.currentItem.dataset.mediaId
      );
      if (active) {
        active.classList.add('is-active');
        this.checkSegmentVisibilty(active);
      }
    }

    /**
     * Ensures the active dot is in the visible area of the progress bar.
     * The bar is its own scroll container (see media-gallery.css), so on a
     * media-heavy product the active dot can sit outside it — same problem the
     * thumbnail rail solves with checkThumbVisibilty, same solution.
     *
     * Measured from rectangles rather than offsetLeft, which is what
     * checkThumbVisibilty uses. offsetLeft is relative to the nearest
     * POSITIONED ancestor — and that is <media-gallery> (it carries .relative),
     * not this bar — so it silently folds in the bar's own inset from the
     * gallery edge and reads in a different coordinate space from scrollLeft.
     * With the bar flush to the gallery the error was zero; once it gained an
     * inline margin and padding it would have been off by that much, roughly a
     * dot's worth. Rects plus scrollLeft are exact whatever the box grows.
     * @param {Element} segment - Active progress bar segment.
     */
    checkSegmentVisibilty(segment) {
      if (!this.progressBar) return;
      if (this.progressBar.scrollWidth <= this.progressBar.clientWidth) return;

      const barRect = this.progressBar.getBoundingClientRect();
      const segRect = segment.getBoundingClientRect();

      // Where the dot sits along the bar's scrollable content, independent of
      // how far the bar happens to be scrolled right now.
      const segStart = segRect.left - barRect.left + this.progressBar.scrollLeft;

      if (segRect.left < barRect.left || segRect.right > barRect.right) {
        // Centre rather than align to the edge: the dots are an at-a-glance
        // position indicator, so the neighbours either side carry the meaning.
        // scrollTo clamps out-of-range values, so the first and last dots come
        // to rest at the ends instead of needing their own special case.
        this.progressBar.scrollTo({
          left: segStart - (this.progressBar.clientWidth - segRect.width) / 2,
          behavior: 'smooth',
        });
      }
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
